import React from 'react';
import { MessageCircle, ShieldCheck, PhoneCall } from 'lucide-react';

export const SupportCenter: React.FC = () => {
    return (
        <div className="animate-fade-in max-w-4xl mx-auto space-y-8 pb-10">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-800/60 p-8 rounded-[2rem] border border-blue-500/20 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl -z-10 animate-pulse-gentle"></div>
                <div>
                    <h2 className="text-3xl font-black text-white flex items-center gap-3 tracking-tight">
                        <MessageCircle className="w-8 h-8 text-blue-400" />
                        Central de Apoio
                    </h2>
                    <p className="text-slate-400 text-sm mt-2 font-medium leading-relaxed max-w-xl">
                        Precisa de ajuda com o carro, faturamento, rotas ou suporte ao cliente? Nossa equipe está pronta para te atender. <strong>Este serviço é 100% gratuito.</strong>
                    </p>
                </div>
                
                <div className="flex items-center gap-3 bg-slate-900/80 p-4 rounded-2xl border border-blue-500/30 shadow-inner z-10 shrink-0">
                    <ShieldCheck className="w-8 h-8 text-emerald-500" />
                    <div>
                        <p className="text-white font-black text-sm tracking-wide">Plantão 24/7</p>
                        <p className="text-emerald-400 text-[10px] font-bold uppercase tracking-wider">Garagem e OPS</p>
                    </div>
                </div>
            </header>

            {/* Grid de Contatos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[
                    { 
                        title: "Garagem / Base", 
                        phone: "+55 11 95059-5802", 
                        desc: "Emergências, B.O.s, problemas mecânicos graves, acidente na pista ou trocas de carro emergencial.", 
                        icon: "🆘", 
                        highlight: false,
                        color: "red"
                    },
                    { 
                        title: "OPS (Operações)", 
                        phone: "+55 11 97346-5905", 
                        desc: "Suporte durante a rota, atrasos na escala, acompanhamento de viagens e problemas de aplicativo do parceiro.", 
                        icon: "🚨", 
                        highlight: false,
                        color: "orange"
                    },
                    { 
                        title: "Plific (Financeiro)", 
                        phone: "+55 11 93503-2895", 
                        desc: "Dúvidas sobre o fechamento de notas, emissão, descontos, deduções no app, adiantamentos e pagamentos.", 
                        icon: "💰", 
                        highlight: true,
                        color: "yellow"
                    },
                    { 
                        title: "RH / Comunidade", 
                        phone: "+55 11 91287-6748", 
                        desc: "Assinatura e renovação de contratos, dúvidas do cadastro, indicações e informações gerais da comunidade.", 
                        icon: "👥", 
                        highlight: false,
                        color: "blue"
                    },
                    { 
                        title: "Exp. Cliente", 
                        phone: "+55 11 93261-3882", 
                        desc: "Central do passageiro. Relatar perda de objetos no carro, elogios e feedbacks do padrão de serviço para o cliente final.", 
                        icon: "⭐", 
                        highlight: false,
                        color: "indigo"
                    },
                ].map((contact, i) => (
                    <a 
                        key={i} 
                        href={`https://wa.me/${contact.phone.replace(/\D/g, '')}`} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`bg-slate-800/40 p-6 rounded-2xl flex flex-col md:flex-row md:items-center gap-5 transition-all group hover:-translate-y-1.5 shadow-lg border relative overflow-hidden backdrop-blur-md
                        ${contact.highlight ? 'border-yellow-500/40 shadow-[0_10px_30px_rgba(234,179,8,0.1)]' : 'border-slate-700/50 hover:border-slate-500 hover:shadow-slate-500/10'}`}
                    >
                        <div className={`absolute top-0 left-0 w-1.5 h-full transition-all duration-300 group-hover:w-full group-hover:opacity-5 -z-10
                            ${contact.color === 'red' ? 'bg-red-500' : 
                              contact.color === 'orange' ? 'bg-orange-500' :
                              contact.color === 'yellow' ? 'bg-yellow-500' :
                              contact.color === 'blue' ? 'bg-blue-500' : 'bg-indigo-500'}
                        `}></div>

                        <div className={`w-14 h-14 shrink-0 rounded-[1.2rem] flex items-center justify-center text-4xl shadow-inner group-hover:rotate-12 transition-transform duration-300 bg-slate-900 border border-slate-700/50`}>
                            {contact.icon}
                        </div>
                        
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="text-white font-black text-lg tracking-wide group-hover:text-blue-200 transition-colors uppercase">{contact.title}</h3>
                                <PhoneCall className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${contact.highlight ? 'text-yellow-500' : 'text-blue-400'}`} />
                            </div>
                            <p className={`text-sm font-black tracking-wider mb-2 ${contact.highlight ? 'text-yellow-400' : 'text-slate-300'}`}>{contact.phone}</p>
                            <p className="text-slate-400 text-xs leading-relaxed font-medium">{contact.desc}</p>
                        </div>
                    </a>
                ))}
            </div>

            {/* Premium CTA / Bottom Area */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 rounded-2xl border border-slate-700 mt-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl relative overflow-hidden">
                <div className="flex items-center gap-4 z-10 w-full">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center border border-blue-500/30 shadow-inner">
                        <MessageCircle className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                        <p className="text-white font-bold text-sm">Tempo de Resposta</p>
                        <p className="text-slate-400 text-xs mt-1 leading-relaxed">Geralmente nossa equipe do WhatsApp responde de forma imediata durante o turno. Tente sempre enviar um áudio curto e objetivo detalhando a situação.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};
